import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  ShieldCheck, KeyRound, Lock, Plus, RefreshCw, Trash2, Copy, Check, AlertTriangle, FileBadge, Save,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function SecuritySection({ siteId, availableScopes }) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ ip_allowlist: [], require_mtls: false });
  const [clients, setClients] = useState([]);
  const [certs, setCerts] = useState([]);
  const [fingerprintHeader, setFingerprintHeader] = useState('X-Client-Cert-Fingerprint');
  const [clientSecretDialog, setClientSecretDialog] = useState(null);
  const [createOauthOpen, setCreateOauthOpen] = useState(false);
  const [uploadCertOpen, setUploadCertOpen] = useState(false);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const [sRes, oRes, cRes] = await Promise.all([
        axios.get(`${API}/portal/sites/${siteId}/security-settings`, { headers: authHeader() }),
        axios.get(`${API}/portal/sites/${siteId}/oauth-clients`, { headers: authHeader() }),
        axios.get(`${API}/portal/sites/${siteId}/client-certificates`, { headers: authHeader() }),
      ]);
      setSettings({
        ip_allowlist: sRes.data?.ip_allowlist || [],
        require_mtls: !!sRes.data?.require_mtls,
      });
      setClients(oRes.data?.data || []);
      setCerts(cRes.data?.data || []);
      setFingerprintHeader(cRes.data?.fingerprint_header || 'X-Client-Cert-Fingerprint');
    } catch (e) {
      console.error(e);
      toast.error('Sicherheitsdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (next) => {
    try {
      const res = await axios.put(
        `${API}/portal/sites/${siteId}/security-settings`,
        next,
        { headers: authHeader() }
      );
      setSettings({
        ip_allowlist: res.data?.ip_allowlist || [],
        require_mtls: !!res.data?.require_mtls,
      });
      toast.success('Sicherheitseinstellungen gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const createOauth = async (body) => {
    try {
      const res = await axios.post(
        `${API}/portal/sites/${siteId}/oauth-clients`,
        body,
        { headers: authHeader() }
      );
      setClientSecretDialog({
        mode: 'created',
        client_id: res.data.client.client_id,
        client_secret: res.data.client_secret,
      });
      setCreateOauthOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'OAuth-Client konnte nicht erstellt werden');
    }
  };

  const rotateOauth = async (clientId) => {
    if (!window.confirm('Client-Secret rotieren? Das neue Secret wird einmalig angezeigt; das alte wird sofort ungültig.')) return;
    try {
      const res = await axios.post(
        `${API}/portal/sites/${siteId}/oauth-clients/${clientId}/rotate-secret`,
        {},
        { headers: authHeader() }
      );
      setClientSecretDialog({
        mode: 'rotated',
        client_id: clientId,
        client_secret: res.data.client_secret,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation fehlgeschlagen');
    }
  };

  const revokeOauth = async (clientId) => {
    const reason = window.prompt('Widerrufs-Grund (optional):') || '';
    if (reason === null) return;
    try {
      await axios.post(
        `${API}/portal/sites/${siteId}/oauth-clients/${clientId}/revoke`,
        { reason },
        { headers: authHeader() }
      );
      toast.success('OAuth-Client widerrufen');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Widerruf fehlgeschlagen');
    }
  };

  const uploadCert = async (body) => {
    try {
      await axios.post(
        `${API}/portal/sites/${siteId}/client-certificates`,
        body,
        { headers: authHeader() }
      );
      toast.success('Zertifikat hochgeladen');
      setUploadCertOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload fehlgeschlagen');
    }
  };

  const revokeCert = async (certId) => {
    const reason = window.prompt('Widerrufs-Grund (optional):') || '';
    if (reason === null) return;
    try {
      await axios.post(
        `${API}/portal/sites/${siteId}/client-certificates/${certId}/revoke`,
        { reason },
        { headers: authHeader() }
      );
      toast.success('Zertifikat widerrufen');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Widerruf fehlgeschlagen');
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-6" data-testid="security-section">
      <Card data-testid="site-security-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-orange-500" />
            Erweiterte Sicherheit (Phase 4)
          </CardTitle>
          <CardDescription>
            IP-Allowlist und Mutual-TLS-Zwang gelten standortweit für alle API-Zugriffe (API-Tokens und OAuth-Clients).
            Einzelne Tokens/Clients können eigene Regeln überschreiben.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <IpAllowlistEditor
            value={settings.ip_allowlist}
            requireMtls={settings.require_mtls}
            onSave={saveSettings}
          />
        </CardContent>
      </Card>

      {/* OAuth 2.0 Clients */}
      <Card data-testid="oauth-clients-card">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="h-5 w-5 text-orange-500" /> OAuth 2.0 Clients
            </CardTitle>
            <CardDescription>Server-zu-Server-Authentifizierung mit <code>client_credentials</code>-Flow. Tauscht Client-ID + Secret gegen ein kurzlebiges JWT.</CardDescription>
          </div>
          <Button onClick={() => setCreateOauthOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="oauth-client-create-btn">
            <Plus className="h-4 w-4 mr-2" /> Client erstellen
          </Button>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm" data-testid="oauth-empty">
              Noch keine OAuth-Clients. Als Alternative zum Bearer-Token empfohlen für Server-zu-Server-Integrationen.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4">Label</th>
                    <th className="py-2 pr-4">Client-ID</th>
                    <th className="py-2 pr-4">Scopes</th>
                    <th className="py-2 pr-4">Sicherheit</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Zuletzt</th>
                    <th className="py-2 pr-4">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => (
                    <tr key={c.client_id} className="border-b hover:bg-muted/30" data-testid={`oauth-row-${c.client_id}`}>
                      <td className="py-3 pr-4 text-xs">{c.label}</td>
                      <td className="py-3 pr-4 font-mono text-xs">{c.client_id}</td>
                      <td className="py-3 pr-4 text-xs">{(c.scopes || []).join(', ')}</td>
                      <td className="py-3 pr-4 text-xs space-x-1">
                        {c.ip_allowlist?.length > 0 && <Badge className="bg-blue-50 text-blue-700 text-[10px]">IP-Lock</Badge>}
                        {c.require_mtls && <Badge className="bg-purple-50 text-purple-700 text-[10px]">mTLS</Badge>}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge className={c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {c.status === 'active' ? 'Aktiv' : 'Widerrufen'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-xs">{c.last_used_at ? new Date(c.last_used_at).toLocaleString('de-DE') : 'nie'}</td>
                      <td className="py-3 pr-4">
                        {c.status === 'active' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => rotateOauth(c.client_id)} data-testid={`oauth-rotate-${c.client_id}`} title="Secret rotieren">
                              <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => revokeOauth(c.client_id)} data-testid={`oauth-revoke-${c.client_id}`} title="Client widerrufen">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
            <p className="font-semibold mb-1">Token-Endpunkt:</p>
            <pre className="font-mono text-[11px] overflow-x-auto">{`POST ${window.location.origin}/api/v1/oauth/token
Content-Type: application/x-www-form-urlencoded

grant_type=client_credentials&client_id=...&client_secret=...&scope=materials:read`}</pre>
          </div>
        </CardContent>
      </Card>

      {/* mTLS Certificates */}
      <Card data-testid="mtls-certs-card">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="h-5 w-5 text-orange-500" /> mTLS-Client-Zertifikate
            </CardTitle>
            <CardDescription>
              Registrieren Sie ein Client-Zertifikat zur gegenseitigen TLS-Authentifizierung. Der Reverse-Proxy terminiert die TLS-Verbindung und reicht den Fingerprint im Header <code>{fingerprintHeader}</code> weiter.
            </CardDescription>
          </div>
          <Button onClick={() => setUploadCertOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="cert-upload-btn">
            <Plus className="h-4 w-4 mr-2" /> Zertifikat hochladen
          </Button>
        </CardHeader>
        <CardContent>
          {certs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm" data-testid="certs-empty">
              Keine Zertifikate registriert.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4">Label</th>
                    <th className="py-2 pr-4">Subject</th>
                    <th className="py-2 pr-4">Fingerprint (SHA-256)</th>
                    <th className="py-2 pr-4">Gültig bis</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map(c => (
                    <tr key={c.cert_id} className="border-b hover:bg-muted/30" data-testid={`cert-row-${c.cert_id}`}>
                      <td className="py-3 pr-4 text-xs">{c.label}</td>
                      <td className="py-3 pr-4 text-xs max-w-[200px] truncate" title={c.subject_dn}>{c.subject_dn}</td>
                      <td className="py-3 pr-4 font-mono text-[10px] max-w-[240px] truncate" title={c.fingerprint_sha256}>{c.fingerprint_sha256}</td>
                      <td className="py-3 pr-4 text-xs">{c.valid_until ? new Date(c.valid_until).toLocaleDateString('de-DE') : '—'}</td>
                      <td className="py-3 pr-4">
                        <Badge className={c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {c.status === 'active' ? 'Aktiv' : 'Widerrufen'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        {c.status === 'active' && (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => revokeCert(c.cert_id)} data-testid={`cert-revoke-${c.cert_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateOauthClientDialog
        open={createOauthOpen}
        onOpenChange={setCreateOauthOpen}
        onSubmit={createOauth}
        availableScopes={availableScopes || []}
      />

      <UploadCertDialog
        open={uploadCertOpen}
        onOpenChange={setUploadCertOpen}
        onSubmit={uploadCert}
      />

      <ClientSecretDialog
        entry={clientSecretDialog}
        onClose={() => setClientSecretDialog(null)}
      />
    </div>
  );
}

// ─── IP Allowlist Editor ───────────────────────────────────────────────

function IpAllowlistEditor({ value, requireMtls, onSave }) {
  const [text, setText] = useState((value || []).join('\n'));
  const [mtls, setMtls] = useState(!!requireMtls);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setText((value || []).join('\n'));
    setMtls(!!requireMtls);
    setDirty(false);
  }, [value, requireMtls]);

  const save = () => {
    const entries = text.split('\n').map(s => s.trim()).filter(Boolean);
    onSave({ ip_allowlist: entries, require_mtls: mtls });
    setDirty(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-semibold">IP-Allowlist (standortweit)</Label>
        <p className="text-xs text-muted-foreground mb-2">Eine Adresse oder CIDR-Range pro Zeile. Leer = alle IPs erlaubt.</p>
        <Textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setDirty(true); }}
          placeholder={'z. B.\n192.168.0.0/24\n203.0.113.5\n2001:db8::/32'}
          className="font-mono text-xs min-h-[100px]"
          data-testid="site-ip-allowlist-textarea"
        />
      </div>
      <label className="flex items-start gap-2 text-sm cursor-pointer border rounded-md p-3 bg-orange-50/40">
        <Checkbox
          checked={mtls}
          onCheckedChange={(v) => { setMtls(!!v); setDirty(true); }}
          data-testid="site-require-mtls"
          className="mt-0.5"
        />
        <div>
          <p className="font-medium">mTLS standortweit erzwingen</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Jeder API-Zugriff muss ein registriertes Client-Zertifikat vorweisen (Fingerprint-Match auf Reverse-Proxy-Header).
          </p>
        </div>
      </label>
      <Button onClick={save} disabled={!dirty} className="bg-orange-500 hover:bg-orange-600" data-testid="site-security-save">
        <Save className="h-4 w-4 mr-2" /> Sicherheitseinstellungen speichern
      </Button>
    </div>
  );
}

// ─── Create OAuth Client Dialog ────────────────────────────────────────

function CreateOauthClientDialog({ open, onOpenChange, onSubmit, availableScopes }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState(['materials:read']);
  const [ipText, setIpText] = useState('');
  const [mtls, setMtls] = useState(false);

  useEffect(() => {
    if (open) { setLabel(''); setScopes(['materials:read']); setIpText(''); setMtls(false); }
  }, [open]);

  const toggleScope = (s) => setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const submit = () => {
    if (!label.trim()) { toast.error('Bitte ein Label vergeben'); return; }
    if (scopes.length === 0) { toast.error('Mindestens ein Scope wählen'); return; }
    onSubmit({
      label: label.trim(),
      scopes,
      ip_allowlist: ipText.split('\n').map(s => s.trim()).filter(Boolean),
      require_mtls: mtls,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="oauth-create-dialog">
        <DialogHeader>
          <DialogTitle>Neuen OAuth-Client erstellen</DialogTitle>
          <DialogDescription>Client-ID und Secret werden nach der Erstellung einmalig angezeigt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="z. B. ERP-Sync Prod" data-testid="oauth-label-input" />
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="grid grid-cols-2 gap-2 mt-1 border rounded-md p-3 max-h-48 overflow-y-auto">
              {availableScopes.map(s => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={scopes.includes(s)} onCheckedChange={() => toggleScope(s)} data-testid={`oauth-scope-${s}`} />
                  <span className="font-mono text-xs">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>IP-Allowlist (optional)</Label>
            <Textarea
              value={ipText}
              onChange={(e) => setIpText(e.target.value)}
              placeholder={'Eine IP oder CIDR pro Zeile.\nLeer = standortweite Einstellung nutzen.'}
              className="font-mono text-xs min-h-[80px]"
              data-testid="oauth-ip-allowlist"
            />
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer border rounded-md p-3">
            <Checkbox checked={mtls} onCheckedChange={(v) => setMtls(!!v)} data-testid="oauth-require-mtls" className="mt-0.5" />
            <div>
              <p className="font-medium">mTLS erzwingen</p>
              <p className="text-xs text-muted-foreground mt-0.5">Client muss ein registriertes Zertifikat vorweisen.</p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} className="bg-orange-500 hover:bg-orange-600" data-testid="oauth-create-submit">Client erstellen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Upload Cert Dialog ────────────────────────────────────────────────

function UploadCertDialog({ open, onOpenChange, onSubmit }) {
  const [label, setLabel] = useState('');
  const [pem, setPem] = useState('');

  useEffect(() => {
    if (open) { setLabel(''); setPem(''); }
  }, [open]);

  const submit = () => {
    if (!label.trim()) { toast.error('Bitte ein Label vergeben'); return; }
    if (!pem.trim()) { toast.error('PEM-Zertifikat ist leer'); return; }
    onSubmit({ label: label.trim(), pem: pem.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="cert-upload-dialog">
        <DialogHeader>
          <DialogTitle>Client-Zertifikat hochladen</DialogTitle>
          <DialogDescription>Fügen Sie den PEM-Inhalt ein. Wir extrahieren Subject, Fingerprint und Gültigkeit automatisch.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="z. B. ERP Prod (2026)" data-testid="cert-label-input" />
          </div>
          <div>
            <Label>PEM-Inhalt</Label>
            <Textarea
              value={pem}
              onChange={(e) => setPem(e.target.value)}
              placeholder={'-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----'}
              className="font-mono text-[10px] min-h-[240px]"
              data-testid="cert-pem-textarea"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} className="bg-orange-500 hover:bg-orange-600" data-testid="cert-upload-submit">
            <FileBadge className="h-4 w-4 mr-2" /> Hochladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Client-Secret Reveal Dialog ───────────────────────────────────────

function ClientSecretDialog({ entry, onClose }) {
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);

  useEffect(() => { setAck(false); setCopied(false); }, [entry]);

  if (!entry) return null;

  const copy = async () => {
    await navigator.clipboard.writeText(`${entry.client_id}:${entry.client_secret}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { if (ack) onClose(); else toast.error('Bitte bestätigen Sie, dass Sie das Secret gespeichert haben.'); } }}>
      <DialogContent className="max-w-xl" data-testid="oauth-secret-modal">
        <DialogHeader>
          <DialogTitle>
            {entry.mode === 'rotated' ? 'Client-Secret rotiert' : 'OAuth-Client erstellt'}
          </DialogTitle>
          <DialogDescription>
            Das Secret wird <strong>nicht erneut angezeigt</strong>. Bitte jetzt sicher speichern.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">Behandeln Sie Client-ID + Secret wie ein Passwort-Paar.</p>
        </div>
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-muted-foreground">Client-ID</Label>
            <div className="bg-slate-950 text-slate-100 p-3 rounded-lg font-mono text-xs break-all" data-testid="oauth-client-id">{entry.client_id}</div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Client-Secret</Label>
            <div className="bg-slate-950 text-slate-100 p-3 rounded-lg font-mono text-xs break-all" data-testid="oauth-client-secret">{entry.client_secret}</div>
          </div>
        </div>
        <Button onClick={copy} variant="outline" className="w-full" data-testid="oauth-copy-btn">
          {copied ? <><Check className="h-4 w-4 mr-2 text-emerald-600" /> Kopiert</> : <><Copy className="h-4 w-4 mr-2" /> client_id:client_secret kopieren</>}
        </Button>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={ack} onCheckedChange={setAck} data-testid="oauth-secret-ack" />
          <span>Ich habe Client-ID und Secret sicher gespeichert.</span>
        </label>
        <DialogFooter>
          <Button onClick={onClose} disabled={!ack} className="bg-orange-500 hover:bg-orange-600" data-testid="oauth-secret-close">Fertig</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
